import { useState, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Box,
  CardHeader,
  CardContent,
  Paper,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Container,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
// components
import Scrollbar from '../../../components/scrollbar';
// sections
import { UserListHead } from '../../../sections/@dashboard/user';

import { Page } from '../../../components';
import { getCategories, resetCategoryState } from '../store/categoryActions';
import { CategoryForm, CategoryEditForm, CategoryDeleteDialog } from '../components';
import { fDate } from '../../../utils/formatTime';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'name', label: 'Category Name', alignRight: false },
  { id: 'code', label: 'Category Code', alignRight: false },
  { id: 'depreciation_rate', label: 'Depreciation Rate', alignRight: false },
  // { id: 'description', label: 'Description', alignRight: false },
  { id: 'created_at', label: 'Created Date', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

// ----------------------------------------------------------------------

export default function Categories() {
  const dispatch = useDispatch();
  const { isLoading, categories, pagination, isSuccess } = useSelector((state) => state.categoryReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    handleOpenEditModal();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAddModal = () => {
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
  };

  const handleOpenEditModal = () => {
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedCategory(null);
  };

  // const handleOpenDeleteDialog = () => {
  //   setOpenDeleteDialog(true);
  //   handleCloseMenu();
  // };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedCategory(null);
  };

  useEffect(() => {
    const payload = {
      page: page + 1,
      rowsPerPage,
    };
    dispatch(getCategories(payload));
  }, [dispatch, page, rowsPerPage]);

  // Refetch categories when operations are successful
  useEffect(() => {
    if (isSuccess) {
      const payload = {
        page: page + 1,
        rowsPerPage,
      };
      dispatch(getCategories(payload));
      // Reset the success state to prevent continuous refetching
      dispatch(resetCategoryState());
    }
  }, [isSuccess, dispatch, page, rowsPerPage]);

  const formatDepreciationRate = (rate) => {
    if (rate === null || rate === undefined) return 'N/A';
    return `${rate}%`;
  };

  const getDepreciationRateColor = (rate) => {
    if (rate === null || rate === undefined) return 'default';
    if (rate <= 10) return 'success';
    if (rate <= 25) return 'warning';
    return 'error';
  };

  // Filter categories based on search term
  const filteredCategories =
    categories?.filter(
      (category) =>
        category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  return (
    <Page title="Asset Categories">
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header Section */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              {/* <CategoryIcon sx={{ fontSize: 32, color: 'primary.main' }} /> */}
              <div>
                <Typography variant="h4" gutterBottom>
                  {''}
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  Manage your asset categories and depreciation rates
                </Typography> */}
              </div>
            </Stack>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddModal} sx={{ borderRadius: 2 }}>
              Add Category
            </Button>
          </Stack>

          {/* Stats Cards */}
          {/* <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main" gutterBottom>
                  {pagination?.count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Categories
                </Typography>
              </Card>
            </Grid>
          </Grid> */}

          {/* Categories Table */}
          <Card>
            <CardHeader
              title="Categories List"
              subheader={`Showing ${filteredCategories.length} of ${pagination?.count || 0} categories`}
              action={
                <TextField
                  size="small"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
              }
            />
            <CardContent>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <ThreeDots
                        height="80"
                        width="80"
                        radius="9"
                        color="#2065d1"
                        ariaLabel="three-dots-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible
                      />
                    </Box>
                  ) : (
                    <Table size="medium">
                      <UserListHead headLabel={TABLE_HEAD} rowCount={filteredCategories.length} />
                      <TableBody>
                        {filteredCategories.length > 0 ? (
                          filteredCategories.map((category) => (
                            <TableRow hover key={category?.id} tabIndex={-1}>
                              <TableCell component="th" scope="row" sx={{ pl: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Typography variant="subtitle2" noWrap>
                                    {category?.name}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              <TableCell align="left">
                                <Chip label={category?.code} size="small" variant="outlined" color="primary" />
                              </TableCell>

                              <TableCell align="left">
                                <Chip
                                  label={formatDepreciationRate(category?.depreciation_rate)}
                                  size="small"
                                  color={getDepreciationRateColor(category?.depreciation_rate)}
                                />
                              </TableCell>

                              {/* <TableCell align="left">
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {category?.description || 'No description'}
                                </Typography>
                              </TableCell> */}

                              <TableCell align="left">
                                <Typography variant="body2" color="text.secondary">
                                  {category?.date_created ? fDate(category.date_created) : 'N/A'}
                                </Typography>
                              </TableCell>

                              <TableCell align="right">
                                <IconButton
                                  size="large"
                                  color="inherit"
                                  onClick={() => handleEditClick(category)}
                                  title="Edit Category"
                                >
                                  <EditIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                              <Alert severity="info">
                                {searchTerm
                                  ? 'No categories match your search criteria.'
                                  : 'No categories found. Click "Add Category" to create your first category.'}
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={pagination?.count || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </Stack>

        {/* Action Menu */}

        {/* Add Category Modal */}
        <CategoryForm open={openAddModal} onClose={handleCloseAddModal} />

        {/* Edit Category Modal */}
        <CategoryEditForm open={openEditModal} onClose={handleCloseEditModal} category={selectedCategory} />

        {/* Delete Confirmation Dialog */}
        <CategoryDeleteDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} category={selectedCategory} />
      </Container>
    </Page>
  );
}
